/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ----------------------------------------------------------------------------
 *   DesignSystemShowcase — the Spec 197 reference "style tile".
 *   Renders every foundation + kit component with the design language so the
 *   look can be approved (light + dark) BEFORE it scales across the portal.
 *   Route: /design-system
 * ============================================================================
 */
import React, { useState } from 'react';
import { HardDrive, Eye, Plus, Search, Globe } from 'lucide-react';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Input,
  Select,
  Checkbox,
  Toggle,
  Badge,
  StatCard,
  EmptyState,
  ErrorState,
  LoadingState,
  Modal,
  Tabs,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <section className="mb-[var(--space-section)]">
    <div className="mb-3">
      <h2 className="text-[length:var(--text-h2)] font-semibold leading-[var(--leading-h2)] text-[var(--color-text-primary)]">{title}</h2>
      {subtitle && <p className="mt-0.5 text-[length:var(--text-sm)] text-[var(--color-text-secondary)]">{subtitle}</p>}
    </div>
    {children}
  </section>
);

const Swatch: React.FC<{ label: string; style: React.CSSProperties; note?: string }> = ({ label, style, note }) => (
  <div className="flex flex-col gap-1.5">
    <div className="h-16 rounded-[var(--radius-md)] border border-[var(--color-border)]" style={style} />
    <div className="text-[length:var(--text-caption)] font-medium text-[var(--color-text-primary)]">{label}</div>
    {note && <div className="text-[length:var(--text-caption)] text-[var(--color-text-secondary)]">{note}</div>}
  </div>
);

interface Row { id: string; name: string; env: string; status: 'ok' | 'warn' | 'fail'; used: string; geo: boolean; }
const ROWS: Row[] = Array.from({ length: 14 }, (_, i) => ({
  id: String(i),
  name: ['Organizations_ODS', 'Compounder_ODS', 'attachments001', 'ddsprodfs', 'healthlinkstore', 'onewaredata'][i % 6] + (i > 5 ? `_${i}` : ''),
  env: ['PROD', 'UAT', 'DEV'][i % 3],
  status: (['ok', 'warn', 'fail'] as const)[i % 3],
  used: ['4.18 TB', '143.45 GB', '0 B', '1.12 TB', '0.03 GB'][i % 5],
  geo: i % 2 === 0,
}));

export const DesignSystemShowcase: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [toggle, setToggle] = useState(true);
  const [check, setCheck] = useState(true);
  const [tab, setTab] = useState('all');

  const columns: DataTableColumn<Row>[] = [
    {
      key: 'name', header: 'Account', width: '34%',
      render: (r) => (
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[var(--radius-sm)] bg-[var(--color-bg-subtle)]">
            <HardDrive className="h-4 w-4 text-[var(--color-accent)]" />
          </span>
          <span className="truncate font-medium text-[var(--color-text-primary)]">{r.name}</span>
        </div>
      ),
    },
    { key: 'env', header: 'Environment', width: '16%', render: (r) => (
      <Badge variant={r.env === 'PROD' ? 'error' : r.env === 'UAT' ? 'warning' : 'secondary'} isOutlined>{r.env}</Badge>
    ) },
    { key: 'status', header: 'Status', width: '16%', render: (r) => (
      <Badge variant={r.status === 'ok' ? 'success' : r.status === 'warn' ? 'warning' : 'error'}>
        {r.status === 'ok' ? 'Healthy' : r.status === 'warn' ? 'Degraded' : 'Failed'}
      </Badge>
    ) },
    { key: 'used', header: 'Used Storage', width: '16%', align: 'right' },
    { key: 'geo', header: 'Geo', width: '8%', render: (r) => (r.geo ? <Globe className="h-4 w-4 text-[var(--color-status-success)]" /> : <span className="text-[var(--color-text-secondary)]">—</span>) },
    { key: 'actions', header: '', width: '10%', align: 'right', render: () => (
      <Button size="sm" variant="ghost"><Eye className="h-4 w-4" /></Button>
    ) },
  ];

  return (
    <PageLayout
      title="Design System"
      description="Spec 197 reference — every foundation and component with the design language. Approve the look here (toggle light/dark) before it scales."
      icon={<HardDrive className="h-6 w-6" />}
      actions={<Button leftIcon={<Plus className="h-4 w-4" />}>Primary action</Button>}
    >
      {/* Foundations */}
      <Section title="Surfaces" subtitle="Layered neutrals — page, card, subtle, sunken, accent, status.">
        <Card>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            <Swatch label="Page" style={{ background: 'var(--color-bg-page)' }} />
            <Swatch label="Card" style={{ background: 'var(--color-bg-card)' }} />
            <Swatch label="Subtle" style={{ background: 'var(--color-bg-subtle)' }} />
            <Swatch label="Sunken" style={{ background: 'var(--color-bg-sunken)' }} />
            <Swatch label="Accent" style={{ background: 'var(--color-accent)' }} />
            <Swatch label="Border" style={{ background: 'var(--color-border)' }} />
            <Swatch label="Success" style={{ background: 'var(--color-status-success)' }} />
            <Swatch label="Warning" style={{ background: 'var(--color-status-warning)' }} />
            <Swatch label="Danger" style={{ background: 'var(--color-status-danger)' }} />
            <Swatch label="Info" style={{ background: 'var(--color-status-info)' }} />
          </div>
        </Card>
      </Section>

      <Section title="Elevation & radius" subtitle="Soft, layered, slate-tinted shadows + one radius scale.">
        <Card>
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
            {(['xs', 'sm', 'md', 'lg'] as const).map((s) => (
              <Swatch key={s} label={`shadow-${s}`} style={{ background: 'var(--color-bg-card)', boxShadow: `var(--shadow-${s})` }} />
            ))}
          </div>
        </Card>
      </Section>

      <Section title="Typography" subtitle="One modular scale, system/Segoe stack.">
        <Card>
          <div className="space-y-3">
            <div className="text-[length:var(--text-display)] font-semibold leading-[var(--leading-display)] text-[var(--color-text-primary)]">Display — page hero</div>
            <div className="text-[length:var(--text-h1)] font-semibold leading-[var(--leading-h1)] text-[var(--color-text-primary)]">H1 — page title</div>
            <div className="text-[length:var(--text-h2)] font-semibold leading-[var(--leading-h2)] text-[var(--color-text-primary)]">H2 — section</div>
            <div className="text-[length:var(--text-h3)] font-semibold leading-[var(--leading-h3)] text-[var(--color-text-primary)]">H3 — card title</div>
            <div className="text-[length:var(--text-body)] leading-[var(--leading-body)] text-[var(--color-text-primary)]">Body — the default reading size for content and tables.</div>
            <div className="text-[length:var(--text-sm)] text-[var(--color-text-secondary)]">Small — secondary / metadata.</div>
            <div className="text-[length:var(--text-caption)] text-[var(--color-text-secondary)]">Caption — labels, captions.</div>
          </div>
        </Card>
      </Section>

      {/* Components */}
      <Section title="Buttons">
        <Card>
          <div className="flex flex-wrap items-center gap-3">
            <Button>Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="danger">Danger</Button>
            <Button variant="success">Success</Button>
            <Button isLoading>Loading</Button>
            <Button disabled>Disabled</Button>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button size="xs">XS</Button>
            <Button size="sm" leftIcon={<Plus className="h-4 w-4" />}>Small</Button>
            <Button size="md">Medium</Button>
            <Button size="lg">Large</Button>
          </div>
        </Card>
      </Section>

      <Section title="Forms">
        <Card>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            <Input label="Search" placeholder="Search accounts…" leftAddon={<Search className="h-4 w-4" />} isFullWidth />
            <Select label="Environment" isFullWidth options={[{ value: 'all', label: 'All' }, { value: 'prod', label: 'PROD' }, { value: 'uat', label: 'UAT' }]} />
            <Input label="With error" errorMessage="This field is required" isFullWidth />
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-6">
            <Checkbox label="Set as primary storage" checked={check} onChange={(e) => setCheck(e.target.checked)} />
            <Toggle label="Auto-refresh" checked={toggle} onChange={setToggle} />
          </div>
        </Card>
      </Section>

      <Section title="Badges">
        <Card>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="success">Healthy</Badge>
            <Badge variant="warning">Degraded</Badge>
            <Badge variant="error">Failed</Badge>
            <Badge variant="info">Info</Badge>
            <Badge variant="primary">Primary</Badge>
            <Badge variant="secondary">Secondary</Badge>
            <Badge variant="success" isOutlined>Outlined</Badge>
            <Badge variant="warning" isOutlined>RA-GRS</Badge>
          </div>
        </Card>
      </Section>

      <Section title="Tabs" subtitle="One canonical tab bar — active accent underline, optional icon + count chip.">
        <Card>
          <Tabs
            value={tab}
            onChange={setTab}
            tabs={[
              { key: 'all', label: 'All Accounts', count: 54 },
              { key: 'geo', label: 'Geo-Redundant', count: 23 },
              { key: 'local', label: 'Local/ZRS', count: 31 },
              { key: 'disabled', label: 'Archived', disabled: true },
            ]}
          />
          <p className="mt-4 text-[length:var(--text-sm)] text-[var(--color-text-secondary)]">Active tab: {tab}</p>
        </Card>
      </Section>

      <Section title="Stat tiles">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard label="Total Accounts" value={54} />
          <StatCard label="Total Storage" value="8.46 TB" valueClassName="text-[var(--color-accent)]" />
          <StatCard label="Geo-Redundant" value={23} valueClassName="text-[var(--color-status-success)]" icon={<Globe className="h-4 w-4" />} />
          <StatCard label="With Containers" value={41} />
        </div>
      </Section>

      <Section title="Data table" subtitle="Dense rows, fixed columns, content-stable across filters.">
        <Card padding="none">
          <DataTable columns={columns} rows={ROWS} rowKey={(r) => r.id} />
        </Card>
      </Section>

      <Section title="States">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card><EmptyState title="No accounts yet" message="Add a storage account to get started." action={<Button size="sm" leftIcon={<Plus className="h-4 w-4" />}>Add</Button>} /></Card>
          <Card><ErrorState title="Failed to load" message="The request timed out." onRetry={() => {}} /></Card>
          <Card><LoadingState rows={4} /></Card>
        </div>
      </Section>

      <Section title="Overlay">
        <Card>
          <Button onClick={() => setOpen(true)}>Open modal</Button>
          <Modal
            isOpen={open}
            onClose={() => setOpen(false)}
            title="Add Storage Account"
            footer={<><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={() => setOpen(false)}>Add Storage Account</Button></>}
          >
            <p className="mb-4 text-[length:var(--text-sm)] text-[var(--color-text-secondary)]">
              Transparent-but-blocking backdrop · framed · movable (drag the header) · no outside-close.
            </p>
            <Input label="Account name" placeholder="mystorageaccount" isFullWidth />
          </Modal>
        </Card>
      </Section>
    </PageLayout>
  );
};

export default DesignSystemShowcase;
